import './styles.scss';
import { DisposalSet, onReadyEach, select, selectAll } from '@neptune/cx/utils';
import {
    addToMultiParamMap,
    removeFromMultiParamMap,
    toMultiParamMap, toURLSearchParams, updateResults
} from '../../fragments/layouts/chroa-search-layout/facets';

const gc = new DisposalSet();

declare const Liferay: {
    ThemeDisplay: {
        getScopeGroupId: () => string;
    };
};

interface TopicTypeaheadResult {
    assetCategoryId: number;
    parentCategoryId?: number;
    title: string;
    score: number;
}

const initCategoryTreeFacet = (facetEl: HTMLDivElement, pageParam: string) => {
    const parentFormEl = facetEl.closest('form');
    const searchLayoutEl = facetEl.closest<HTMLDivElement>('.chroa-search-layout');
    if (!parentFormEl || !searchLayoutEl) return;

    const facetParamName = select<HTMLInputElement>(parentFormEl, 'input.facet-parameter-name');
    const treeItemCheckboxEls = selectAll<HTMLInputElement>(facetEl, '.chroa-category-tree-facet__item input[type="checkbox"]');

    for (const checkboxEl of treeItemCheckboxEls) {
        (gc.register(checkboxEl)
            .addListener('change', (e) => {
                const multiParamMap = toMultiParamMap(new URLSearchParams(window.location.search));
                const value = checkboxEl.value;
                if (checkboxEl.checked) {
                    addToMultiParamMap(multiParamMap, facetParamName.value, value);
                } else {
                    removeFromMultiParamMap(multiParamMap, facetParamName.value, value);
                }
                removeFromMultiParamMap(multiParamMap, pageParam, null);
                const params = toURLSearchParams(multiParamMap);
                const nextUrl = `${window.location.pathname}?${params}`;
                updateResults(searchLayoutEl, nextUrl, pageParam);
            })
        );
    }

    initTopicSearch(facetEl);
};

// Debounced fetch-as-you-type plus an explicit submit path (button click /
// Enter), both running the identical match-and-expand routine — the submit
// path exists so a user who doesn't want to rely on live-updating results
// (slow connections, mobile virtual keyboards, screen readers/motor
// impairments) can commit their query explicitly. Neither path checks a
// topic's checkbox or navigates away from the page — it only narrows the
// visible top-level list and expands the accordions that matched. Registered
// via the same `gc` DisposalSet as the checkbox listeners so it's torn down
// and re-bound for free by the existing reinit()/AJAX-refresh lifecycle.
//
// The search row is a plain <div>, not a <form> — this facet's whole markup
// already renders inside the search-results page's outer <form> (the one
// index.ts's checkbox listeners walk up to via facetEl.closest('form')), and
// HTML forbids nested forms. A <form> here would parse as invalid and the
// browser silently drops its opening tag, taking every descendant selector
// down with it — caught via an actual browser run (CHRO-1076's e2e suite),
// not by template/type checking. The submit button is `type="button"`
// (no native form to submit) and Enter is handled explicitly in the
// `keydown` listener below alongside Escape.
const initTopicSearch = (facetEl: HTMLDivElement) => {
    const submitButtonEl = select<HTMLButtonElement>(facetEl, '.chroa-category-tree-facet__search-submit');
    const searchInputEl = select<HTMLInputElement>(facetEl, '.chroa-category-tree-facet__search-input');
    const statusEl = select<HTMLElement>(facetEl, '.chroa-category-tree-facet__search-status');
    const topLevelItemEls = selectAll<HTMLLIElement>(facetEl, '.chroa-category-tree-facet__top-level-item');
    const vocabId = facetEl.dataset.vocabId;

    let abortController: AbortController | null = null;
    let debounceTimer: ReturnType<typeof setTimeout> | undefined;

    const clearSearchState = () => {
        for (const itemEl of topLevelItemEls) {
            itemEl.classList.remove('chroa-category-tree-facet__top-level-item--hidden');
            for (const checkboxEl of selectAll<HTMLElement>(itemEl, '.chroa-category-tree-facet__term-checkbox--matched')) {
                checkboxEl.classList.remove('chroa-category-tree-facet__term-checkbox--matched');
            }
            for (const termItemEl of selectAll<HTMLElement>(itemEl, '.chroa-category-tree-facet__item--hidden')) {
                termItemEl.classList.remove('chroa-category-tree-facet__item--hidden');
            }
        }
        statusEl.textContent = '';
    };

    // Rather than leave every term visible and merely highlight the matched
    // leaf (the original behavior), unmatched terms are hidden outright so
    // a match reads as "here's the topic" instead of "find the bold one in
    // this list". The `--matched` class is still set (kept for the e2e
    // suite's assertions and as a hook for any future need) but no longer
    // carries a visual style of its own.
    const applyMatches = (matchedParentIds: Set<string>, matchedTermIds: Set<string>, query: string) => {
        let visibleCount = 0;
        for (const itemEl of topLevelItemEls) {
            const titleEl = select<HTMLElement>(itemEl, '.chroa-accordion__title-text');
            const titleMatches = (titleEl.textContent ?? '').toLowerCase().includes(query);
            const descendantMatches = matchedParentIds.has(itemEl.dataset.categoryId ?? '');
            const visible = titleMatches || descendantMatches;
            itemEl.classList.toggle('chroa-category-tree-facet__top-level-item--hidden', !visible);

            // Recomputed unconditionally (not just when descendantMatches is
            // true) so a term left over as matched/hidden from a previous
            // keystroke never lingers once this keystroke's result no longer
            // supports it — e.g. this accordion matched by title only this
            // time, or stopped matching entirely.
            for (const checkboxEl of selectAll<HTMLElement>(itemEl, '.chroa-category-tree-facet__term-checkbox')) {
                checkboxEl.classList.toggle(
                    'chroa-category-tree-facet__term-checkbox--matched',
                    descendantMatches && matchedTermIds.has(checkboxEl.dataset.termId ?? '')
                );
            }
            for (const termItemEl of selectAll<HTMLElement>(itemEl, '.chroa-category-tree-facet__item')) {
                const hasMatchWithin = termItemEl.querySelector('.chroa-category-tree-facet__term-checkbox--matched') !== null;
                termItemEl.classList.toggle('chroa-category-tree-facet__item--hidden', descendantMatches && !hasMatchWithin);
            }

            if (visible && descendantMatches) {
                visibleCount++;
                const detailsEl = itemEl.querySelector<HTMLDetailsElement>('details');
                if (detailsEl) detailsEl.open = true;
            } else if (visible) {
                visibleCount++;
            }
        }
        statusEl.textContent = visibleCount === 0
            ? 'No topics found'
            : `${visibleCount} topic${visibleCount === 1 ? '' : 's'} found`;
    };

    // Shared by the debounced `input` path and the explicit submit path
    // (button click / Enter) — the only difference between the two callers is
    // *when* this runs, never *what* it does.
    const runSearch = async (query: string) => {
        clearTimeout(debounceTimer);
        abortController?.abort();

        if (query.length < 2) {
            clearSearchState();
            return;
        }

        abortController = new AbortController();
        try {
            const scopeGroupId = Liferay.ThemeDisplay.getScopeGroupId();
            const response = await fetch(
                `/o/chroa-topic-typeahead/v1.0/typeahead/topic/${scopeGroupId}/${vocabId}?q=${encodeURIComponent(query)}`,
                { signal: abortController.signal }
            );
            if (!response.ok) {
                clearSearchState(); // fail open — full list, no error UI
                return;
            }
            const { topicTypeaheadResults } = await response.json() as { topicTypeaheadResults: TopicTypeaheadResult[] };
            const matchedParentIds = new Set(topicTypeaheadResults.map(r => String(r.parentCategoryId ?? r.assetCategoryId)));
            const matchedTermIds = new Set(topicTypeaheadResults.map(r => String(r.assetCategoryId)));
            applyMatches(matchedParentIds, matchedTermIds, query);
        } catch (e) {
            if ((e as Error).name !== 'AbortError') clearSearchState();
        }
    };

    gc.register(searchInputEl).addListener('input', () => {
        const query = searchInputEl.value.trim().toLowerCase();
        clearTimeout(debounceTimer);
        if (query.length < 2) {
            abortController?.abort();
            clearSearchState();
            return;
        }
        debounceTimer = setTimeout(() => runSearch(query), 300);
    });

    gc.register(searchInputEl).addListener('keydown', (e) => {
        const key = (e as KeyboardEvent).key;
        if (key === 'Escape') {
            searchInputEl.value = '';
            clearTimeout(debounceTimer);
            abortController?.abort();
            clearSearchState();
        } else if (key === 'Enter') {
            // Explicit path, bypassing the debounce entirely. No native <form>
            // exists here (see the note above initTopicSearch), so there's no
            // navigation to prevent — this only re-runs the same
            // narrow-and-expand routine on demand.
            e.preventDefault();
            runSearch(searchInputEl.value.trim().toLowerCase());
        }
    });

    // Explicit path: submit button click, running the identical routine.
    gc.register(submitButtonEl).addListener('click', () => {
        runSearch(searchInputEl.value.trim().toLowerCase());
    });
};

// CHRO-1099: the AJAX facet-apply flow replaces this whole widget with a
// freshly server-rendered copy (see reinit() below), so every <details>
// accordion reverts to its FreeMarker-default closed state on each request —
// including the section the user just interacted with. Captured/restored by
// top-level category id (stable across requests) rather than DOM position,
// since the top-level list can itself be re-sorted/filtered per request.
const getOpenCategoryIds = (facetEl: HTMLDivElement): Set<string> => {
    const openCategoryIds = new Set<string>();
    for (const itemEl of selectAll<HTMLLIElement>(facetEl, '.chroa-category-tree-facet__top-level-item')) {
        const categoryId = itemEl.dataset.categoryId;
        const detailsEl = itemEl.querySelector<HTMLDetailsElement>('details');
        if (categoryId && detailsEl?.open) openCategoryIds.add(categoryId);
    }
    return openCategoryIds;
};

const restoreOpenCategoryIds = (facetEl: HTMLDivElement, openCategoryIds: Set<string>) => {
    if (openCategoryIds.size === 0) return;
    for (const itemEl of selectAll<HTMLLIElement>(facetEl, '.chroa-category-tree-facet__top-level-item')) {
        const categoryId = itemEl.dataset.categoryId;
        if (!categoryId || !openCategoryIds.has(categoryId)) continue;
        const detailsEl = itemEl.querySelector<HTMLDetailsElement>('details');
        if (detailsEl) detailsEl.open = true;
    }
};

export const reinit = (parentEl: HTMLDivElement, replacements: HTMLDivElement[], pageParam: string) => {
    gc.disposeAll();
    const facetEls = selectAll<HTMLDivElement>(parentEl, '.chroa-category-tree-facet');
    for (let i = 0; i < facetEls.length; i++) {
        const facetEl = facetEls[i];
        const replacementFacetEl = replacements[i];

        if (replacementFacetEl) {
            const openCategoryIds = getOpenCategoryIds(facetEl);
            facetEl.replaceWith(replacementFacetEl);
            restoreOpenCategoryIds(replacementFacetEl, openCategoryIds);
            initCategoryTreeFacet(replacementFacetEl, pageParam);
        }
    }
}

onReadyEach<HTMLDivElement>('.chroa-category-tree-facet', facetEl => {
    initCategoryTreeFacet(facetEl, 'start')
});

if (import.meta.hot) {
    import.meta.hot.accept();
    import.meta.hot.dispose(() => gc.disposeAll());
}
